<template>
  <div class="payouts-to-card-page">
    <transactions-page-controls
      :is-transactions-loading="isPayoutsLoading"
      :filter-object="filterObject"
      :is-filter-active="filterActive"
      :additional-filters="$options.ADDITIONAL_FILTERS"
      :available-filter-types="$options.AVAILABLE_FILTER_TYPES"
      :stats-abac="{ data: 'payout-to-card-statistcs', action: 'read' }"
      :stats="payoutsToCardStats"
      :export-abac="{ data: 'payout-to-card-export-registry', action: 'read' }"
      @export="exportPayoutsToCard"
      @refresh="loadPayoutsToCard"
      @filterChanged="handleFilterChanged"
    >
      <template #specificActions>
        <require-access action="read" data="payout-to-card-make_payout">
          <primary-button
            class="payouts-to-card-actions-bar__create-bill"
            :label="$t('payouts.to-card.actions.create')"
            @click="handleCreatePayoutToCardAction"
          ></primary-button>
        </require-access>
      </template>
    </transactions-page-controls>
    <payouts-to-card-table
      :is-transactions-loading="isPayoutsLoading"
      class="company-payouts-to-card__table"
      :transactions-list="payoutsToCardTransactions"
    ></payouts-to-card-table>
  </div>
</template>

<script>
import { ADD_EXPORT } from '@vue-app/store/const/actions-types'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import moment from 'moment'
import responsiveMixin from '@vue-app/mixins/responsive-mixin'

import { mapGetters, mapState } from 'vuex'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import PayoutsToCardTable from '@vue-app/components/company/payouts/to-card/table/PayoutsToCardTable.vue'
import { getCookie, setCookie } from '@js-app/services/helpers/cookie'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import TransactionsPageControls from '@vue-app/components/company/transactions/shared/transactions-page-controls/TransactionsPageControls.vue'
import { Stats } from '@js-app/entities/transaction/Stats'
import {
  ADDITIONAL_FILTERS,
  AVAILABLE_FILTER_TYPES,
} from '@js-app/const/transactions-filters'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'

export default {
  name: 'PayoutsToCard',
  components: {
    PrimaryButton,
    TransactionsPageControls,
    RequireAccess,
    PayoutsToCardTable,
  },
  props: {},
  mixins: [responsiveMixin],
  DEFAULT_FILTER_OBJECT: {
    dates: {
      from: moment().subtract(1, 'days').toDate(),
      to: moment().toDate(),
    },
    currentFilterType: null,
    activeFilters: {},
  },
  data() {
    return {
      payoutsToCardStats: new Stats(),
      payoutsToCardTransactions: [],
      filterActive: false,
      isPayoutsLoading: false,
      filterObject: null,
    }
  },

  AVAILABLE_FILTER_TYPES,
  ADDITIONAL_FILTERS,

  computed: {
    ...mapGetters(['exportExisted']),
    ...mapState(['selectedCompany']),
    activeFilters() {
      const _filters = {}
      const activeFilters = this.filterObject?.activeFilters || {}

      for (const _filterKey in activeFilters) {
        const filter = activeFilters[_filterKey]

        if (!filter) continue

        let value =
            filter.filterValue !== undefined && filter.filterValue !== null
                ? filter.filterValue
                : filter

        if (_filterKey === 'cardPan') {
          if (/\*/.test(value)) {
            value = value.replace(/\*+/g, '%')
          }
        }

        _filters[_filterKey] = value

        if (_filterKey === 'status' && _filters[_filterKey]?.statuses) {
          _filters[_filterKey] = _filters[_filterKey].statuses
        }
      }

      return _filters
    },
  },
  watch: {
    filterObject: {
      deep: true,
      handler: 'loadPayoutsToCard',
    },
    selectedCompany: {
      deep: true,
      handler: 'startupPage',
      immediate: true,
    },
  },
  methods: {
    startupPage() {
      try {
        let _payoutsToCardFilter = JSON.parse(
          getCookie(`payouts.filter.${this.selectedCompany}`)
        )

        if (_payoutsToCardFilter) {
          this.filterObject = _payoutsToCardFilter
          this.filterActive = true
        } else {
          this.filterObject = this.$options.DEFAULT_FILTER_OBJECT
        }
      } catch (e) {
        console.info('payouts_filters_not_found')
      } finally {
      }
    },
    async loadPayoutsToCard() {
      if (this.isPayoutsLoading) {
        return false
      }

      this.isPayoutsLoading = true
      try {
        let payoutsToCardDataResponse =
          await CompaniesApi.getCompanyPayoutsToCard(this.selectedCompany, {
            date_from: moment(this.filterObject.dates.from).format(
              'YYYY-MM-DD 00:00:00'
            ),
            date_to: moment(this.filterObject.dates.to).format(
              'YYYY-MM-DD 23:59:59'
            ),
            filters: this.activeFilters,
          })

        this.payoutsToCardStats = new Stats(
          payoutsToCardDataResponse.data.Stats
        )
        this.payoutsToCardTransactions =
          payoutsToCardDataResponse.data.ReportOperations || []
      } catch (e) {
        this.payoutsToCardTransactions = []
        console.error('failed load analytics data', e)
      } finally {
        this.isPayoutsLoading = false
      }
    },
    handleFilterChanged(newFilterObject, changeType) {
      this.filterObject = { ...newFilterObject }

      this.filterActive = changeType === 'apply'

      if (changeType === 'reset') {
        setCookie(`bills.filter.${this.id}`, null, 0)
      } else if (changeType === 'apply') {
        setCookie(
          `bills.filter.${this.id}`,
          JSON.stringify(this.filterObject),
          0.5
        )
      }
    },
    async exportPayoutsToCard(exportType) {
      let _filtersForExport = { ...this.activeFilters }

      let requestObject = {
        date_from: moment(this.filterObject.dates.from).format(
          'YYYY-MM-DD 00:00:00'
        ),
        date_to: moment(this.filterObject.dates.to).format(
          'YYYY-MM-DD 23:59:59'
        ),
        filters: _filtersForExport,
        type: exportType.id,
      }

      let _filterString = JSON.stringify(requestObject)

      if (this.exportExisted('payouts', _filterString)) {
        return
      }

      let _payoutsExportResponse =
        await CompaniesApi.exportCompanyPayoutsToCard(
          this.selectedCompany,
          requestObject
        )

      await this.$store.dispatch(ADD_EXPORT, {
        ..._payoutsExportResponse.data,
        section: 'payouts',
        filterString: _filterString,
      })
    },
    handleCreatePayoutToCardAction() {
      this.$router.push(
        RouteHelper.getRoute('create-payout-to-card', {
          companyId: this.selectedCompany,
        })
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.company-payouts-to-card {
  &__actions-bar {
    margin-top: 30px;
  }

  &__statistic {
    margin-top: 15px;
  }

  &__filters {
    margin-top: 15px;
  }

  &__table {
    margin-top: 10px;
  }
}
</style>
